// /official-results — the Electoral-Commission-published result
// composition page. Reached from the `official_results` homepage
// phase's prominent CTA band (and direct URL).
//
// COMPOSES three pre-existing pieces, each independently toggleable
// by a feature flag so editorial can hide an element if its source
// isn't trusted yet (e.g. commission-final summary published but
// our own vote-flow tallies still in flux):
//
//   1. ResultsMap          (sectionKey: official_results_map)
//      — the cube map, showing per-district seats by party.
//   2. National + per-district summary (official_results_summary)
//      — NationalRollup (seats per party nationally) + a compact
//        per-district seat-split table.
//   3. Vote-flow / breakdown (official_results_vote_flow)
//      — district picker + DistrictResultsCard (whose RoundTable
//        subcomponent IS the round-by-round per-candidate breakdown).
//
// Honesty contract:
//   · Each FeatureSection wraps one element. status='live' renders
//     the element; status='coming_soon' shows the placeholder card;
//     status='hidden' renders nothing. The placeholder + hidden
//     branches let editorial say "summary is final, vote-flow
//     pending verification" with one toggle each.
//   · Demo banner: same results_demo_mode discipline as elsewhere.
//     The page renders ONE demo banner at the top when active; each
//     widget already has its own inline ⚠ Demo / Test Data chip via
//     useResultsData / useResultsRoster.
//   · No editorial nationalCall here. The Hero on the official_results
//     homepage variant carries the framing; this page is composition.

import { useState } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import FeatureSection from '../FeatureSection';
import ResultsMap from './ResultsMap';
import NationalRollup from './NationalRollup';
import DistrictResultsCard from './DistrictResultsCard';
import { useResultsData } from './useResultsData';
import { useResultsRoster } from './useResultsRoster';
import {
  DISTRICT_NUMBERS,
  deriveDistrictCount,
} from '../../data/electionday-results-types';
import { partyChip, partyColour } from './useResultsRoster';
import './Results.css';

export default function OfficialResultsPage() {
  return (
    <>
      <Header />
      <OfficialResultsBody />
      <Footer />
    </>
  );
}

// Shared body — also mounted on the homepage by HomepagePostElection
// (post_election phase). Kept as a default-exported page above for
// the dedicated /official-results URL.
export function OfficialResultsBody({
  showIntro = true,
}: {
  showIntro?: boolean;
}) {
  const { isDemo } = useResultsData();
  const [districtId, setDistrictId] = useState<number>(1);
  const [expandAll, setExpandAll] = useState<boolean>(false);

  return (
    <main
      style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '32px 22px 64px',
      }}
    >
      {showIntro && (
        <header style={{ marginBottom: 24 }}>
          <p
            style={{
              margin: 0,
              fontFamily: "var(--font-display, 'Poppins', sans-serif)",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: '#003da5',
            }}
          >
            Electoral Commission · Official
          </p>
          <h1
            style={{
              margin: '6px 0 8px',
              fontFamily: "var(--font-display, 'Poppins', sans-serif)",
              fontWeight: 900,
              fontSize: 'clamp(28px, 4vw, 44px)',
              letterSpacing: -0.01,
              color: 'var(--ink, #222)',
            }}
          >
            Official results
          </h1>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-body, serif)',
              fontSize: 15,
              lineHeight: 1.55,
              color: 'var(--muted, #666)',
              maxWidth: 720,
            }}
          >
            Per-district seats elected, the national rollup, and the
            full round-by-round vote-flow for each district — as
            published by the Electoral Commission. Each section below
            can be independently hidden from{' '}
            <code>/admin/official-results</code> if the data source
            for that element isn't trusted yet.
          </p>
          {isDemo && (
            <p
              role="status"
              style={{
                marginTop: 14,
                padding: '8px 12px',
                background: 'rgba(232,184,0,0.18)',
                border: '1px dashed #d4830a',
                color: '#5a4400',
                borderRadius: 6,
                fontFamily: "var(--font-display, 'Poppins', sans-serif)",
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              ⚠ Demo / test data — page rendering against the
              results_demo_mode dataset, not the real commission
              source.
            </p>
          )}
        </header>
      )}

      {/* ── Element 1 — Results map ────────────────────────── */}
      <FeatureSection
        sectionKey="official_results_map"
        label="Official results — map"
      >
        <section style={{ marginBottom: 28 }}>
          <h2 style={sectionHeadStyle}>Map</h2>
          <ResultsMap title="Official results map" />
        </section>
      </FeatureSection>

      {/* ── Element 2 — Summary (national + per-district) ──── */}
      <FeatureSection
        sectionKey="official_results_summary"
        label="Official results — summary table"
      >
        <section style={{ marginBottom: 28 }}>
          <h2 style={sectionHeadStyle}>Summary</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(260px, 380px) 1fr',
              gap: 24,
              alignItems: 'start',
            }}
          >
            <NationalRollup />
            <PerDistrictSeatTable />
          </div>
        </section>
      </FeatureSection>

      {/* ── Element 3 — Vote-flow / breakdown ──────────────── */}
      <FeatureSection
        sectionKey="official_results_vote_flow"
        label="Official results — vote-flow"
      >
        <section>
          <h2 style={sectionHeadStyle}>Vote-flow by district</h2>
          <p style={voteFlowSubtitleStyle}>
            Every candidate&apos;s running total, the transfer that
            fired, who was elected or excluded each round. Pick one
            district below, or expand all thirteen to see the full
            count side by side.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <DistrictPicker
              selected={districtId}
              onSelect={setDistrictId}
              disabled={expandAll}
            />
            <button
              type="button"
              onClick={() => setExpandAll((v) => !v)}
              aria-pressed={expandAll}
              style={{
                appearance: 'none',
                border: expandAll
                  ? '2px solid #003da5'
                  : '1px solid var(--border, #d8d8d8)',
                background: expandAll
                  ? 'rgba(0,61,165,0.08)'
                  : 'transparent',
                color: expandAll ? '#003da5' : 'var(--ink, #222)',
                fontFamily: "var(--font-display, 'Poppins', sans-serif)",
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: 0.6,
                padding: '6px 12px',
                borderRadius: 4,
                cursor: 'pointer',
                textTransform: 'uppercase',
              }}
            >
              {expandAll ? '← Show one' : 'Expand all 13'}
            </button>
          </div>
          <div style={{ marginTop: 16 }}>
            {expandAll ? (
              <div
                style={{
                  display: 'grid',
                  gap: 28,
                }}
              >
                {DISTRICT_NUMBERS.map((n) => (
                  <DistrictResultsCard key={n} districtId={n} />
                ))}
              </div>
            ) : (
              <DistrictResultsCard districtId={districtId} />
            )}
          </div>
        </section>
      </FeatureSection>
    </main>
  );
}

// ── Per-district seat-split summary table ───────────────────────
//
// Compact 13-row table: District N | <party chip>×N seats. Reads
// through useResultsData so demo + real both render via the same
// path. Lives in this page only — if other surfaces need it later
// extract to its own component.

function PerDistrictSeatTable() {
  const { data } = useResultsData();
  const { byDistrict, byId } = useResultsRoster();

  return (
    <section className="results-card" aria-label="Per-district seat summary">
      <p className="results-card__kicker">By district</p>
      <h3 className="results-card__title">Seats per district</h3>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginTop: 10,
          fontFamily: "var(--font-display, 'Poppins', sans-serif)",
          fontSize: 13,
        }}
      >
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border, #d8d8d8)' }}>
            <th style={thStyle('left')}>District</th>
            <th style={thStyle('left')}>Elected (in order)</th>
            <th style={thStyle('right')}>Seats / 5</th>
          </tr>
        </thead>
        <tbody>
          {DISTRICT_NUMBERS.map((n) => {
            const candidateIds = byDistrict(n).map((c) => c.id);
            const derived = deriveDistrictCount(
              n,
              data.districts[String(n)],
              candidateIds,
            );
            const seats = derived.electedSeats;
            return (
              <tr
                key={n}
                style={{ borderBottom: '1px solid var(--border-alt, #f0f0f0)' }}
              >
                <th scope="row" style={tdStyle('left', true)}>
                  D{n}
                </th>
                <td style={tdStyle('left')}>
                  {seats.length === 0 ? (
                    <span style={{ color: 'var(--muted, #666)', fontStyle: 'italic' }}>
                      Awaiting
                    </span>
                  ) : (
                    <span
                      style={{
                        display: 'inline-flex',
                        gap: 4,
                        flexWrap: 'wrap',
                      }}
                    >
                      {seats.map((cid, i) => {
                        const cand = byId(cid);
                        if (!cand) return null;
                        return (
                          <span
                            key={`${n}-${i}`}
                            title={cand.name}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '2px 6px',
                              borderRadius: 3,
                              background: partyColour(cand.party),
                              color: '#fff',
                              fontSize: 10,
                              fontWeight: 800,
                              letterSpacing: 0.5,
                            }}
                          >
                            {partyChip(cand.party)}
                          </span>
                        );
                      })}
                    </span>
                  )}
                </td>
                <td style={tdStyle('right')}>
                  <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                    {seats.length}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

// ── District picker for the vote-flow section ───────────────────

function DistrictPicker({
  selected,
  onSelect,
  disabled = false,
}: {
  selected: number;
  onSelect: (n: number) => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="tablist"
      aria-label="Districts"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        opacity: disabled ? 0.45 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {DISTRICT_NUMBERS.map((n) => {
        const active = n === selected;
        return (
          <button
            key={n}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={disabled}
            onClick={() => onSelect(n)}
            style={{
              appearance: 'none',
              border: active ? '2px solid #003da5' : '1px solid var(--border, #d8d8d8)',
              background: active ? 'rgba(0,61,165,0.08)' : 'transparent',
              color: active ? '#003da5' : 'var(--ink, #222)',
              fontFamily: "var(--font-display, 'Poppins', sans-serif)",
              fontWeight: 800,
              fontSize: 12,
              letterSpacing: 0.6,
              padding: '6px 12px',
              borderRadius: 4,
              cursor: active ? 'default' : 'pointer',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            D{n}
          </button>
        );
      })}
    </div>
  );
}

// ── styles ──────────────────────────────────────────────────────

const sectionHeadStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontFamily: "var(--font-display, 'Poppins', sans-serif)",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: 'var(--muted, #666)',
};

const voteFlowSubtitleStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontFamily: 'var(--font-body, serif)',
  fontSize: 14,
  color: 'var(--muted, #666)',
};

const thStyle = (align: 'left' | 'right' | 'center'): React.CSSProperties => ({
  textAlign: align,
  padding: '8px 8px',
  fontWeight: 700,
  fontSize: 10,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: 'var(--muted, #666)',
});

const tdStyle = (align: 'left' | 'right' | 'center', strong = false): React.CSSProperties => ({
  textAlign: align,
  padding: '6px 8px',
  verticalAlign: 'middle',
  fontWeight: strong ? 800 : 500,
});
